import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { TableFooterEnrichAnyway } from "@/components/TableFooter/table-footer.types";
import {
	useEnrichAnywayMutation,
	useRetryNotFoundMutation,
} from "@/pages/Private/redux/csvEnrichment/csvEnrichment.api";

import { ListPerson } from "../../../../schema/list";
import {
	isRecoverableReason,
	RECOVERABLE_FILTER_PARAM,
} from "../../../../utils/exclusionReasonMeta";
import { EnrichAnywayModal } from "./EnrichAnywayModal";
import { EnrichAnywayProgressModal } from "./EnrichAnywayProgressModal";
import { RetryOthersModal } from "./RetryOthersModal";

/** What a lead's Retry tells the user it did (PREC-4520). */
const retryMessage = (
	t: (key: string, options?: Record<string, unknown>) => string,
	queued: boolean,
	notQueuedReason: string | null | undefined,
	pendingOthers: number
): string => {
	if (!queued) {
		return t(`inbox.enrichAnywayFlow.retryNotQueued.${notQueuedReason ?? "NOTHING_TO_RETRY"}`);
	}

	return pendingOthers > 0
		? t("inbox.enrichAnywayFlow.retryStartedWithOthers", { count: pendingOthers })
		: t("inbox.enrichAnywayFlow.retryStarted");
};

/** Per-lead recovery cost — the 0.9-credit `ENRICH_AFTER_SCREENING` remainder. */
const RECOVERY_CREDITS_PER_LEAD = 0.9;

interface UseEnrichAnywayParams {
	listId?: number;
	data: ListPerson[];
	selectedItems: number[];
	/** Applies the PREC-3544 "Last added/enriched" sort (list_person.updatedAt DESC). */
	onSortByLastEnriched: () => void;
	/** Surfaces confirmation / error snackbars. */
	onNotify: (message: string) => void;
}

const isRecoverableRow = (row: ListPerson): boolean =>
	!!row.exclusionReasons?.some((reason) => isRecoverableReason(reason.filterParam));

/** The excluded title of a recovered row: the jobTitle reason's filterValue (fallback: prospect title). */
const rowTitle = (row: ListPerson): string | undefined =>
	row.exclusionReasons?.find((reason) => reason.filterParam === RECOVERABLE_FILTER_PARAM)
		?.filterValue ||
	row.prospect?.title ||
	undefined;

/**
 * Owns the "Enrich anyway" recovery flow (PREC-3536 Phase 5): the footer action over the
 * selection, the per-row quick actions, the confirm + progress modals and the plain retry.
 */
export const useEnrichAnyway = ({
	listId,
	data,
	selectedItems,
	onSortByLastEnriched,
	onNotify,
}: UseEnrichAnywayParams) => {
	const { t } = useTranslation();
	const [targetRows, setTargetRows] = useState<ListPerson[] | null>(null);
	const [progressCount, setProgressCount] = useState<number | null>(null);
	const [enrichAnyway, { isLoading }] = useEnrichAnywayMutation();
	const [retryNotFound, { isLoading: isRetrying }] = useRetryNotFoundMutation();
	const [retryOthers, setRetryOthers] = useState<{ row: ListPerson; pendingOthers: number } | null>(
		null
	);

	const recoverableSelection = useMemo(
		() => data.filter((row) => selectedItems.includes(row.id) && isRecoverableRow(row)),
		[data, selectedItems]
	);

	const openForRows = (rows: ListPerson[]) => {
		if (rows.length > 0) {
			setTargetRows(rows);
		}
	};

	/** Footer action — present only while the selection contains recoverable leads. */
	const footerEnrichAnyway: TableFooterEnrichAnyway | undefined =
		recoverableSelection.length > 0
			? {
					label: t("inbox.enrichAnywayFlow.footerButton"),
					costLabel: t("inbox.enrichAnywayFlow.footerCost", {
						cost: (recoverableSelection.length * RECOVERY_CREDITS_PER_LEAD).toFixed(1),
					}),
					onOpen: () => openForRows(recoverableSelection),
				}
			: undefined;

	const handleConfirm = async (whitelist: boolean) => {
		if (!listId || !targetRows) {
			return;
		}

		const count = targetRows.length;

		try {
			await enrichAnyway({
				listId,
				listPersonIds: targetRows.map((row) => row.id),
				whitelist,
			}).unwrap();

			setTargetRows(null);
			setProgressCount(count);
		} catch (error) {
			onNotify((error as { data?: { message?: string } })?.data?.message ?? String(error));
		}
	};

	/**
	 * Search the lead again (PREC-4520). The answer says whether anything was queued and, if not,
	 * why; the message says which: it used to announce a retry that, on a person list, never ran
	 * (PREC-4524). When the list's run would also search other waiting people, it asks first
	 * (OTHERS_WAITING) and searches only once the user agreed.
	 */
	const handleRetry = async (row: ListPerson, includeOthers = false) => {
		if (!listId) {
			return;
		}

		try {
			const { queued, notQueuedReason, pendingOthers } = await retryNotFound({
				listId,
				listPersonIds: [row.id],
				...(includeOthers && { includeOthers: true }),
			}).unwrap();

			if (!queued && notQueuedReason === "OTHERS_WAITING") {
				setRetryOthers({ row, pendingOthers: pendingOthers ?? 0 });

				return;
			}

			setRetryOthers(null);
			onNotify(retryMessage(t, queued, notQueuedReason, pendingOthers ?? 0));
		} catch (error) {
			setRetryOthers(null);
			onNotify((error as { data?: { message?: string } })?.data?.message ?? String(error));
		}
	};

	const titles = useMemo(
		() => [
			...new Set((targetRows ?? []).map(rowTitle).filter((title): title is string => !!title)),
		],
		[targetRows]
	);

	const renderEnrichAnywayModals = () => (
		<>
			<EnrichAnywayModal
				count={targetRows?.length ?? 0}
				isLoading={isLoading}
				isOpened={!!targetRows}
				titles={titles}
				onClose={() => setTargetRows(null)}
				onConfirm={handleConfirm}
			/>
			<RetryOthersModal
				isLoading={isRetrying}
				pendingOthers={retryOthers?.pendingOthers ?? null}
				onClose={() => setRetryOthers(null)}
				onConfirm={() => retryOthers && handleRetry(retryOthers.row, true)}
			/>
			<EnrichAnywayProgressModal
				count={progressCount ?? 0}
				isOpened={progressCount !== null}
				onClose={() => setProgressCount(null)}
				onSortByEnriched={() => {
					setProgressCount(null);
					onSortByLastEnriched();
				}}
			/>
		</>
	);

	return {
		footerEnrichAnyway,
		openEnrichAnywayForRow: (row: ListPerson) => openForRows([row]),
		retryRow: (row: ListPerson) => handleRetry(row),
		renderEnrichAnywayModals,
	};
};
